/**
 * Insights service — thin Axios wrapper for the matching-insights
 * endpoints (design §5 Phase 2 items 4 & 6): rule-synthesis proposals
 * and the read-only measurement stats, plus the pure presentation
 * helpers the Insikter page and the "skapa regel?" CTA use.
 *
 * Mirrors MatchingService.js / RulesService.js: pure functions that take
 * a companyId + payload and return axios promises. Callers own loading
 * state and errors — the backend may not be deployed yet, so every
 * caller must render an error/empty state rather than crash.
 *
 * Contract (backend routes.js →
 * /:companyId/accounting/reconciliation/insights):
 *   GET  /rule-proposals            → { success, proposals: [...] }
 *   POST /rule-proposals/dismiss    → { counterparty } (503 = NOT dismissed)
 *   GET  /stats/overview            → { success, bank: {...}, tax_authority: {...} }
 *   GET  /stats/acceptance?since=   → { success, since, acceptance: [...] }
 *   GET  /stats/calibration         → { success, calibration: [...] }
 *   GET  /stats/timeliness          → { success, booked_lines, median_days_import_to_booking, basis }
 *   GET  /embedding-stats           → { success, vectors_total, by_source: { sie, booking },
 *                                       added_last_7d, provider, model, live }  (always 200)
 *   GET  /calibration/readiness     → { success, gate: {...}, readiness: [...], curve: [...] }
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';

const config = { withCredentials: true };

const base = (companyId) => `${companyId}/accounting/reconciliation/insights`;
const autopostBase = (companyId) => `${companyId}/accounting/reconciliation/autopost`;

// ---------------------------------------------------------------------------
// Labels (Swedish — raw enum codes must never reach the UI)
// ---------------------------------------------------------------------------

/** Matching layers as stored in matching_feedback.layer. */
export const LAYER_LABELS = {
    deterministic: 'Deterministisk',
    rule: 'Regler',
    history: 'Historik',
    embedding: 'Likhet',
    llm: 'AI',
    none: 'Ingen',
};

/** Label for a layer code — unknown codes are humanized, never raw. */
export function layerLabel(code) {
    if (code && LAYER_LABELS[code]) return LAYER_LABELS[code];
    const words = String(code || '').replace(/[_-]+/g, ' ').trim();
    if (!words) return 'Okänd';
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// Calibration bucket labels: the backend emits '0.6-0.7' … '0.9-1.0'
// plus the below_0.6 / no_confidence catch-alls.
const BUCKET_SPECIALS = {
    'below_0.6': 'Under 0,6',
    no_confidence: 'Utan konfidens',
};

/** Swedish label for a calibration bucket ('0.6-0.7' → '0,6–0,7'). */
export function bucketLabel(bucket) {
    if (bucket == null || bucket === '') return 'Okänd';
    if (BUCKET_SPECIALS[bucket]) return BUCKET_SPECIALS[bucket];
    return String(bucket).replace(/\./g, ',').replace(/-/g, '–');
}

/**
 * Format a backend rate (fraction 0–1, or null when the denominator was
 * zero) as a Swedish percentage, e.g. 0.8235 → '82,4 %'. null/absent →
 * '–' (absence is tolerated, never guessed — same rule as the backend).
 */
export function formatRate(value) {
    const v = parseFloat(value);
    if (value == null || !Number.isFinite(v)) return '–';
    return `${new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 1 }).format(v * 100)} %`;
}

/**
 * Format a fraction (0–1, or null when the denominator was zero) as a
 * Swedish percentage — the readiness table's accept_rate / wilson_lower.
 * Same absence-is-a-dash rule as formatRate (which it delegates to).
 */
export function formatPct(value) {
    return formatRate(value);
}

/**
 * Eligibility badge for one calibration-readiness row → { variant, label }.
 * Driven by the row's own gate verdict (eligible + reason), never recomputed:
 *   eligible          → grön  "Redo"
 *   too little data    → grå   "För lite data"  (reason: 'insufficient data …')
 *   below the bound    → gul   "Under gräns"
 * The full backend reason is shown separately as a tooltip.
 */
export function eligibilityBadge(row = {}) {
    if (row && row.eligible) return { variant: 'success', label: 'Redo' };
    const reason = String(row?.reason || '').toLowerCase();
    if (reason.includes('insufficient')) return { variant: 'secondary', label: 'För lite data' };
    return { variant: 'warning', label: 'Under gräns' };
}

// ---------------------------------------------------------------------------
// Consistency-check layer (design §4.8, Phase 4 item 2) — pure helpers
// ---------------------------------------------------------------------------

/** finding_type → Swedish label. Raw enum codes must never reach the UI. */
export const CONSISTENCY_LABELS = {
    inconsistent_counterparty: 'Inkonsekvent kontering',
    amount_outlier: 'Avvikande belopp',
    missing_vat_split: 'Saknad momsdel',
    duplicate_suspect: 'Möjlig dubblett',
    off_class: 'Ovanligt konto',
};

/** Label for a finding_type — unknown codes are humanized, never raw. */
export function consistencyLabel(code) {
    if (code && CONSISTENCY_LABELS[code]) return CONSISTENCY_LABELS[code];
    const words = String(code || '').replace(/[_-]+/g, ' ').trim();
    if (!words) return 'Avvikelse';
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Severity badge for a consistency finding → { variant, label }.
 * The backend emits only 'info' | 'warning'; anything else is treated as info.
 */
export function severityBadge(severity) {
    if (severity === 'warning') return { variant: 'warning', label: 'Varning' };
    return { variant: 'secondary', label: 'Info' };
}

/**
 * One-line Swedish summary for a finding's detail_json. The backend always
 * stamps a Swedish `message`; we prefer it, tolerate a JSON string or object,
 * and never surface raw JSON to the user (falls back to the label).
 */
export function consistencyDetailSummary(finding = {}) {
    let detail = finding.detail_json;
    if (typeof detail === 'string') {
        try { detail = JSON.parse(detail); } catch (_) { return detail; }
    }
    if (detail && typeof detail === 'object' && detail.message) return String(detail.message);
    return consistencyLabel(finding.finding_type);
}

/**
 * Find the rule proposal that matches a just-processed statement line,
 * fuzzily: the proposal's normalized counterparty appears in the line's
 * reference or description (lowercased). Returns the first hit or null.
 */
export function findMatchingProposal(proposals = [], line = {}) {
    const haystack = `${line?.reference || ''} ${line?.description || ''}`.toLowerCase();
    if (!haystack.trim()) return null;
    for (const proposal of proposals || []) {
        const needle = String(proposal?.counterparty || '').toLowerCase().trim();
        if (needle && haystack.includes(needle)) return proposal;
    }
    return null;
}

/**
 * Map a proposal's suggestedRule to the `rule` prop RuleFormModal seeds
 * from (no id → create mode). Defensive against partial payloads: the
 * modal always gets name/conditions/action objects it can read.
 */
export function proposalToFormRule(proposal = {}) {
    const suggested = proposal?.suggestedRule || {};
    return {
        name: suggested.name || (proposal?.counterparty ? `Auto: ${proposal.counterparty}` : ''),
        statement_source: suggested.statement_source || 'bank',
        conditions: suggested.conditions || {},
        action: suggested.action && Array.isArray(suggested.action.rows)
            ? suggested.action
            : { rows: [] },
        auto_apply: false, // proposals never pre-arm auto-booking (§4.5)
        active: true,
    };
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /insights/rule-proposals
 * Returns { success, proposals: [{ counterparty, occurrences, lastSeen,
 *   derived_from, suggestedRule }] }.
 */
export async function getRuleProposals(companyId) {
    const url = getApiUrl(`${base(companyId)}/rule-proposals`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /insights/rule-proposals/dismiss  Body: { counterparty }
 * 503 = the dismissal was NOT persisted — callers must surface that as
 * an error (a silently dropped dismissal would resurface the proposal).
 */
export async function dismissRuleProposal(companyId, counterparty) {
    const url = getApiUrl(`${base(companyId)}/rule-proposals/dismiss`);
    const { data } = await axios.post(url, { counterparty }, config);
    return data;
}

/**
 * GET /insights/stats/overview
 * Returns { success, bank: { total, by_status, unmatched, …,
 *   coverage_excl_llm, coverage_incl_llm }, tax_authority: { total, by_status } }.
 */
export async function getOverviewStats(companyId) {
    const url = getApiUrl(`${base(companyId)}/stats/overview`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /insights/stats/acceptance?since=YYYY-MM-DD
 * Rows: { layer, engine_version, suggested, accepted, edited, rejected,
 *   acceptance_rate, top1_rate, correction_ratio, … }.
 */
export async function getAcceptanceStats(companyId, { since } = {}) {
    const url = getApiUrl(`${base(companyId)}/stats/acceptance`);
    const params = { ...(since ? { since } : {}) };
    const { data } = await axios.get(url, { ...config, params });
    return data;
}

/**
 * GET /insights/stats/calibration
 * Rows: { engine_version, bucket, shown, decided, accept_without_edit_rate, … }.
 */
export async function getCalibrationStats(companyId) {
    const url = getApiUrl(`${base(companyId)}/stats/calibration`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /insights/stats/timeliness
 * Returns { success, booked_lines, median_days_import_to_booking, basis }.
 */
export async function getTimelinessStats(companyId) {
    const url = getApiUrl(`${base(companyId)}/stats/timeliness`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /insights/embedding-stats
 * Vector-corpus growth for the "vektorminne" tile. ALWAYS 200 — when the
 * store/provider are unconfigured the backend returns live:false + zeros
 * (the caller renders an OFF state, never an error).
 * Returns { success, vectors_total, by_source: { sie, booking },
 *   added_last_7d, provider, model, live }.
 */
export async function getEmbeddingStats(companyId) {
    const url = getApiUrl(`${base(companyId)}/embedding-stats`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /insights/calibration/readiness
 * The learned auto-post gate. Returns { success, gate: { wilson_confidence,
 *   wilson_lower_min, min_decisions }, readiness: [{ engine_version, layer,
 *   transaction_class, statement_source, decisions, accepted, edited,
 *   rejected, accept_rate, wilson_lower, eligible, reason }], curve: [...] }.
 */
export async function getCalibrationReadiness(companyId) {
    const url = getApiUrl(`${base(companyId)}/calibration/readiness`);
    const { data } = await axios.get(url, config);
    return data;
}

// ---------------------------------------------------------------------------
// Auto-post governance (feature 'auto_post', Phase 4 item 1)
// Base: /:companyId/accounting/reconciliation/autopost
// Every caller must tolerate 403/404 (feature absent/not deployed) and hide.
// ---------------------------------------------------------------------------

/**
 * GET /autopost/settings
 * Returns { success, kill_switch, classes: [{ transaction_class, enabled,
 *   amount_cap, calibration_eligible, accept_rate, wilson_lower, decisions,
 *   readiness_reason, enabled_by, enabled_at }] } — the union of measured
 * calibration classes and enabled toggles, joined server-side.
 */
export async function getAutoPostSettings(companyId) {
    const url = getApiUrl(`${autopostBase(companyId)}/settings`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /autopost/settings  Body: { transaction_class, enabled, amount_cap? }
 * Enables/disables ONE class + sets its cap. The backend still enforces the
 * calibration Wilson gate — enabling an unearned class is inert (400/no-op),
 * so the UI must disable the toggle until the class is eligible.
 */
export async function setAutoPostClass(companyId, { transactionClass, enabled, amountCap } = {}) {
    const url = getApiUrl(`${autopostBase(companyId)}/settings`);
    const body = {
        transaction_class: transactionClass,
        enabled: !!enabled,
        ...(amountCap != null && amountCap !== '' ? { amount_cap: parseFloat(amountCap) } : {}),
    };
    const { data } = await axios.post(url, body, config);
    return data;
}

/**
 * POST /autopost/killswitch  Body: { on: boolean }
 * The instant per-tenant brake — when on, NO class auto-posts.
 */
export async function setAutoPostKillSwitch(companyId, on) {
    const url = getApiUrl(`${autopostBase(companyId)}/killswitch`);
    const { data } = await axios.post(url, { on: !!on }, config);
    return data;
}

// ---------------------------------------------------------------------------
// Consistency-check findings (feature 'auto_post', Phase 4 item 2)
// Base: /:companyId/accounting/reconciliation/insights/consistency
// ---------------------------------------------------------------------------

/**
 * GET /insights/consistency?finding_type=&severity=
 * Returns { success, findings: [{ id, verification_id, statement_line_id,
 *   finding_type, severity, detail_json, status, created_at }] } — open only.
 */
export async function getConsistencyFindings(companyId, { findingType, severity } = {}) {
    const url = getApiUrl(`${base(companyId)}/consistency`);
    const params = {
        ...(findingType ? { finding_type: findingType } : {}),
        ...(severity ? { severity } : {}),
    };
    const { data } = await axios.get(url, { ...config, params });
    return data;
}

/**
 * POST /insights/consistency/:id/dismiss
 * 200 { success, id } | 404 (no open finding) | 503 (storage absent).
 */
export async function dismissConsistencyFinding(companyId, id) {
    const url = getApiUrl(`${base(companyId)}/consistency/${id}/dismiss`);
    const { data } = await axios.post(url, {}, config);
    return data;
}
